import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { 
  ClipboardCheck, ArrowLeft, Save, BookOpen, Plus, Trash2, 
  GripVertical, CheckCircle2, Circle 
} from 'lucide-react';
import { toast } from 'sonner';

const CreateEvaluationPage = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetCourse = searchParams.get('course');
  const presetModule = searchParams.get('module');
  const presetLesson = searchParams.get('lesson');
  // Where to go back after saving (the course editor, if we came from it)
  const returnTo = presetCourse ? `/admin/courses/${presetCourse}/edit` : '/dashboard';

  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    course_id: '',
    module_id: '',
    lesson_ids: [],
    title: '',
    description: '',
    duration_minutes: 30,
    passing_score: 60,
    max_attempts: 1,
    shuffle_questions: true,
    show_results: true
  });

  // Questions state
  const [questions, setQuestions] = useState([
    { 
      id: 1, 
      question_text: '', 
      question_type: 'single_choice', 
      options: ['', '', '', ''], 
      correct_answer: '',
      correct_options: [],
      points: 1 
    }
  ]);

  useEffect(() => {
    fetchCourses();
    // When launched from a lesson, preselect the course/module/lesson automatically
    if (presetCourse) {
      preselectFromParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await api.get('/courses');
      setCourses(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des cours');
    }
  };

  const preselectFromParams = async () => {
    try {
      const response = await api.get(`/courses/${presetCourse}`);
      const mods = response.data.modules || [];
      setModules(mods);
      if (presetModule) {
        const selectedModule = mods.find(m => m.id.toString() === presetModule.toString());
        setLessons(selectedModule?.lessons || []);
      }
      setFormData(prev => ({
        ...prev,
        course_id: presetCourse.toString(),
        module_id: presetModule ? presetModule.toString() : '',
        lesson_ids: presetLesson ? [presetLesson.toString()] : []
      }));
    } catch (error) {
      // Silent: user can still pick manually
    }
  };

  const fetchModules = async (courseId) => {
    try {
      const response = await api.get(`/courses/${courseId}`);
      setModules(response.data.modules || []);
      setLessons([]);
      setFormData(prev => ({ ...prev, module_id: '', lesson_ids: [] }));
    } catch (error) {
      toast.error('Erreur lors du chargement des modules');
    }
  };

  const handleCourseChange = (value) => {
    setFormData(prev => ({ ...prev, course_id: value }));
    fetchModules(value);
  };

  const handleModuleChange = (value) => {
    setFormData(prev => ({ ...prev, module_id: value, lesson_ids: [] }));
    const selectedModule = modules.find(m => m.id.toString() === value);
    setLessons(selectedModule?.lessons || []);
  };

  const toggleLesson = (lessonId) => {
    setFormData(prev => {
      const exists = prev.lesson_ids.includes(lessonId);
      return {
        ...prev,
        lesson_ids: exists
          ? prev.lesson_ids.filter(id => id !== lessonId)
          : [...prev.lesson_ids, lessonId]
      };
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Question management
  const addQuestion = () => {
    const newId = Math.max(...questions.map(q => q.id), 0) + 1;
    setQuestions([...questions, {
      id: newId,
      question_text: '',
      question_type: 'single_choice',
      options: ['', '', '', ''],
      correct_answer: '',
      correct_options: [],
      points: 1
    }]);
  };

  // Toggle a correct option (handles single vs multiple choice)
  const toggleCorrectOption = (question, option) => {
    if (!option) return;
    if (question.question_type === 'multi_select') {
      const current = question.correct_options || [];
      const next = current.includes(option)
        ? current.filter(o => o !== option)
        : [...current, option];
      updateQuestion(question.id, 'correct_options', next);
    } else {
      updateQuestion(question.id, 'correct_answer', option);
    }
  };

  const isOptionCorrect = (question, option) => {
    if (!option) return false;
    if (question.question_type === 'multi_select') {
      return (question.correct_options || []).includes(option);
    }
    return question.correct_answer === option;
  };

  // When switching question type, reset the correct answer selections
  const changeQuestionType = (id, type) => {
    setQuestions(questions.map(q =>
      q.id === id ? { ...q, question_type: type, correct_answer: '', correct_options: [] } : q
    ));
  };

  const removeQuestion = (id) => {
    if (questions.length > 1) {
      setQuestions(questions.filter(q => q.id !== id));
    } else {
      toast.error('L\'évaluation doit contenir au moins une question');
    }
  };

  const updateQuestion = (id, field, value) => {
    setQuestions(questions.map(q => 
      q.id === id ? { ...q, [field]: value } : q
    ));
  };

  const updateOption = (questionId, optionIndex, value) => {
    setQuestions(questions.map(q => {
      if (q.id === questionId) {
        const newOptions = [...q.options];
        newOptions[optionIndex] = value;
        return { ...q, options: newOptions };
      }
      return q;
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.lesson_ids.length === 0 || !formData.title) {
      toast.error('Veuillez sélectionner au moins une leçon et renseigner un titre');
      return;
    }

    // Validate questions
    const validQuestions = questions.filter(q => {
      if (!q.question_text) return false;
      if (q.question_type === 'multi_select') {
        return (q.correct_options || []).length > 0;
      }
      return !!q.correct_answer;
    });
    if (validQuestions.length === 0) {
      toast.error('Ajoutez au moins une question avec une réponse correcte');
      return;
    }

    setLoading(true);
    try {
      // Create one quiz per selected lesson (quiz is attached to a single lesson)
      for (const lessonId of formData.lesson_ids) {
        const quizResponse = await api.post('/quizzes', {
          lesson_id: parseInt(lessonId),
          title: formData.title,
          description: formData.description,
          passing_score: formData.passing_score
        });

        const quizId = quizResponse.data.id;

        // Add questions
        for (let i = 0; i < validQuestions.length; i++) {
          const q = validQuestions[i];
          const correctAnswer = q.question_type === 'multi_select'
            ? JSON.stringify((q.correct_options || []).filter(o => o.trim() !== '').sort())
            : q.correct_answer;
          await api.post('/quiz-questions', {
            quiz_id: quizId,
            question_text: q.question_text,
            question_type: q.question_type,
            options: q.options.filter(o => o.trim() !== ''),
            correct_answer: correctAnswer,
            points: q.points,
            order: i
          });
        }
      }

      toast.success('Évaluation créée avec succès ! Les étudiants ont été notifiés.');
      navigate(returnTo);
    } catch (error) {
      console.error('Error creating evaluation:', error);
      toast.error('Erreur lors de la création de l\'évaluation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Créer une Évaluation" onMenuClick={onMenuClick} />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate(returnTo)}
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {presetCourse ? 'Retour au cours' : 'Retour au tableau de bord'}
        </Button>

        <form onSubmit={handleSubmit}>
          {/* Course Selection */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-blue-600" />
                Sélection du Cours
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Cours *</Label>
                  <Select value={formData.course_id} onValueChange={handleCourseChange}>
                    <SelectTrigger data-testid="select-course">
                      <SelectValue placeholder="Sélectionner un cours" />
                    </SelectTrigger>
                    <SelectContent>
                      {courses.map(course => (
                        <SelectItem key={course.id} value={course.id.toString()}>
                          {course.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Module *</Label>
                  <Select 
                    value={formData.module_id} 
                    onValueChange={handleModuleChange}
                    disabled={!formData.course_id}
                  >
                    <SelectTrigger data-testid="select-module">
                      <SelectValue placeholder="Sélectionner un module" />
                    </SelectTrigger>
                    <SelectContent>
                      {modules.map(module => (
                        <SelectItem key={module.id} value={module.id.toString()}>
                          {module.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Leçon(s) *</Label>
                  <p className="text-xs text-slate-400 mb-2">
                    {formData.module_id
                      ? 'Sélectionnez une ou plusieurs leçons'
                      : 'Sélectionnez d\'abord un module'}
                  </p>
                </div>
              </div>

              {formData.module_id && (
                <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 max-h-56 overflow-y-auto space-y-1">
                  {lessons.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-2">
                      Aucune leçon dans ce module
                    </p>
                  ) : (
                    lessons.map(lesson => (
                      <label
                        key={lesson.id}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          formData.lesson_ids.includes(lesson.id.toString())
                            ? 'bg-blue-50 dark:bg-blue-900/20'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-700'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.lesson_ids.includes(lesson.id.toString())}
                          onChange={() => toggleLesson(lesson.id.toString())}
                          className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                          data-testid={`lesson-checkbox-${lesson.id}`}
                        />
                        <span className="text-sm text-slate-900 dark:text-white">{lesson.name}</span>
                      </label>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Evaluation Details */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-blue-600" />
                Paramètres de l'Évaluation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">Titre de l'évaluation *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Ex: Quiz JavaScript - Les Fondamentaux"
                  data-testid="input-title"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-2 block">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Instructions pour les étudiants..."
                  rows={2}
                  data-testid="input-description"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Durée (minutes)</Label>
                  <Input
                    type="number"
                    min="5"
                    max="180"
                    value={formData.duration_minutes}
                    onChange={(e) => handleInputChange('duration_minutes', parseInt(e.target.value))}
                    data-testid="input-duration"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Score de passage (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.passing_score}
                    onChange={(e) => handleInputChange('passing_score', parseInt(e.target.value))}
                    data-testid="input-passing-score"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Tentatives max</Label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.max_attempts}
                    onChange={(e) => handleInputChange('max_attempts', parseInt(e.target.value))}
                    data-testid="input-max-attempts"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-4 mt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.shuffle_questions}
                    onChange={(e) => handleInputChange('shuffle_questions', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm">Mélanger les questions</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.show_results}
                    onChange={(e) => handleInputChange('show_results', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm">Afficher les résultats à la fin</span>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Questions */}
          <Card className="mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                Questions ({questions.length})
              </CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addQuestion}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une question
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {questions.map((question, qIndex) => (
                <div 
                  key={question.id} 
                  className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div className="flex items-center gap-2 text-slate-400">
                      <GripVertical className="w-4 h-4" />
                      <span className="font-medium text-slate-600 dark:text-slate-300">Q{qIndex + 1}</span>
                    </div>
                    <div className="flex-1">
                      <Input
                        value={question.question_text}
                        onChange={(e) => updateQuestion(question.id, 'question_text', e.target.value)}
                        placeholder="Saisissez votre question..."
                        className="font-medium"
                        data-testid={`question-${qIndex}-text`}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={question.points}
                        onChange={(e) => updateQuestion(question.id, 'points', parseInt(e.target.value))}
                        className="w-20"
                        placeholder="Points"
                      />
                      <Button 
                        type="button"
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeQuestion(question.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="ml-9 mb-3">
                    <Label className="text-xs text-slate-500 mb-2 block">Type de question</Label>
                    <Select
                      value={question.question_type}
                      onValueChange={(v) => changeQuestionType(question.id, v)}
                    >
                      <SelectTrigger className="w-full md:w-64" data-testid={`question-${qIndex}-type`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single_choice">Choix unique</SelectItem>
                        <SelectItem value="multi_select">Choix multiple</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="ml-9 space-y-2">
                    <Label className="text-xs text-slate-500 mb-2 block">
                      {question.question_type === 'multi_select'
                        ? 'Cliquez sur les options pour les marquer comme réponses correctes'
                        : 'Cliquez sur une option pour la marquer comme réponse correcte'}
                    </Label>
                    {question.options.map((option, oIndex) => (
                      <div 
                        key={oIndex} 
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                          isOptionCorrect(question, option)
                            ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-700'
                        }`}
                        onClick={() => toggleCorrectOption(question, option)}
                      >
                        {isOptionCorrect(question, option) ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                        ) : (
                          <Circle className="w-5 h-5 text-slate-300 flex-shrink-0" />
                        )}
                        <Input
                          value={option}
                          onChange={(e) => updateOption(question.id, oIndex, e.target.value)}
                          placeholder={`Option ${oIndex + 1}`}
                          className="flex-1"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`question-${qIndex}-option-${oIndex}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Add question at the bottom so no need to scroll up */}
              <Button
                type="button"
                variant="outline"
                className="w-full border-dashed"
                onClick={addQuestion}
                data-testid="add-question-bottom"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une question
              </Button>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button 
              type="button" 
              variant="outline"
              onClick={() => navigate(returnTo)}
            >
              Annuler
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="submit-evaluation"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Création en cours...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  Créer l'évaluation
                </span>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default CreateEvaluationPage;
